<template>
    <div class="metrics-configuration-data-grid">
        <div class="top">
            <h4>{{ headerName }}</h4>
            <div
                class="close"
                @click="close?.()"
            >
                <al-icon icon="cross-inverted" />
            </div>
        </div>
        <DataFilters
            v-if="acl('edit_configure_metrics')"
            :select-all="selectAll"
            :selected-count="selectedCount"
            :grid-api="(gridApi as any)"
        />
        <div>
            <ag-grid-vue
                style="height: 300px"
                class="ag-theme-alpine"
                :column-defs="columnDefinitions"
                :row-data="rowData"
                row-selection="multiple"
                :on-grid-ready="onGridReady"
                :on-selection-changed="onGridSelectionChanged"
                :on-filter-changed="onGridFilterChanged"
                :modules="modules"
                :suppress-scroll-on-new-data="true"
                :suppress-row-click-selection="true"
            />
        </div>
    </div>
</template>
<script setup lang="ts">
import { MetricStatusCustomField } from '@/components';
import { METRIC_STATUS, RowData } from '@/types';
import { GridApi, GridReadyEvent } from '@ag-grid-community/core';
import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridVue } from '@ag-grid-community/vue3';
import {
    computed, defineEmits, defineProps, ref, watch
} from 'vue';
import DataFilters from './DataFilters.vue';

import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { Module } from '@ag-grid-community/core';
import { useACLs } from '@agyletime/vue-3-common/src/composables/acl';
import { useFeatureFlow } from '@agyletime/vue-3-common/src/composables/featureflow';
import { cloneDeep } from 'lodash';
import MetricLabelCustomField from './MetricLabelCustomField.vue';
import { MetricsConfigColumnDefinitionWithLabel } from '@/types/MetricsConfigColumnDefinitionWithLabel';
const modules = [ClientSideRowModelModule] as Module[];

const emits = defineEmits(['updateChildren']);

interface Props {
    headerName?: string;
    children: MetricsConfigColumnDefinitionWithLabel[];
    close?: () => void;
}

const { acl } = useACLs();
const props = defineProps<Props>();
const selectAll = ref<boolean>(false);
const selectedCount = ref<number>(0);
const gridApi = ref<GridApi<RowData> | null>(null);
const ff = useFeatureFlow();

//
// Grid Functions, Handlers & Watchers
//
watch(
    () => props.headerName,
    () => {
        selectedCount.value = 0;
        selectAll.value = false;
    }
);

type GridRowType = {
    metrics: string | undefined;
    label: string;
    status: string;
    type: string | undefined;
    format: string;
    permission: string;
    metricDefinition: MetricsConfigColumnDefinitionWithLabel;
    field: string;
    subCategory: string | undefined;
    category: string | undefined;
};
const rowData = computed(() =>
    props.children.map((columnDef) => ({
        metrics: columnDef.headerName,
        label: columnDef.label ?? '',
        status: METRIC_STATUS[+(columnDef.isSelected || false)],
        type: columnDef.dataType,
        format: '',
        permission: '',
        metricDefinition: columnDef,
        field: columnDef.field ?? '',
        subCategory: columnDef.parent?.headerName,
        category: columnDef.parent?.parent?.headerName
    }))
);

const updateRow = (value: GridRowType) => {
    const children = cloneDeep(props.children);

    for (const [ idx, child ] of children.entries()) {
        if (child.field === value.field) {
            children[idx] = value.metricDefinition;
            children[idx].label = value.label;
        }
    }

    emits('updateChildren', children);
};

const columnDefinitions = [
    {
        headerName: 'Metrics',
        field: 'metrics',
        checkboxSelection: acl('edit_configure_metrics'),
        filter: 'agTextColumnFilter',
        resizable: true
    },
    {
        headerName: 'Custom Label',
        field: 'label',
        filter: 'agTextColumnFilter',
        cellRenderer: MetricLabelCustomField,
        cellRendererParams: { gridApi, updateRow },
        hide: ff('ica-98-custom-labels-datagrid-column').isOff
    },
    {
        headerName: 'Status',
        field: 'status',
        filter: 'agTextColumnFilter',
        cellRenderer: MetricStatusCustomField,
        cellRendererParams: { gridApi }
    },
    {
        headerName: 'Type',
        field: 'type',
        cellClass: 'data-type'
    }
    // { headerName: 'Format', field: 'format' }, // TODO: Uncomment once work is done - hiding for now
    // { headerName: 'Permission', field: 'permission' } // TODO: Uncomment once work is done - hiding for now
];

// Obtain API from grid's onGridReady event
const onGridReady = (evt: GridReadyEvent) => {
    evt.api.sizeColumnsToFit();
    gridApi.value = evt.api;
};

const toggleSelectAllIfAllSelected = () => {
    if (!acl('edit_configure_metrics')) return;
    if (gridApi.value) {
        let currentVisibleRows = 0;
        let currentSelectedVisibleRows = 0;

        gridApi.value.forEachNodeAfterFilter((row) => {
            currentVisibleRows++;
            if (row.isSelected()) currentSelectedVisibleRows++;
        });

        selectAll.value = currentSelectedVisibleRows === currentVisibleRows && currentVisibleRows !== 0;
    }
};
// Keep up to date with selection changes to ensure that the selected count is always accurate.
const onGridSelectionChanged = () => {
    if (gridApi.value) {
        selectedCount.value = gridApi.value.getSelectedRows()?.length || 0;
        toggleSelectAllIfAllSelected();
    }
};

// Display "Select all" as toggled when 2/2 are selected
// Display "Select all" as un-toggled when 2/20 are selected
// Ensure that when moving from a filtered grid to un-filtered,
// that the toggle is reflective of the new state.
const onGridFilterChanged = () => {
    toggleSelectAllIfAllSelected();
};
</script>
<style lang="scss">
@import './style.scss';
</style>
